import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Folder, FolderOpen, File, Upload, Plus, Loader, Link2, Trash2, Download,
  Check, ChevronRight, ArrowUp, Copy, Pencil, X, Archive, FileArchive, Share2,
  LayoutGrid, List, Move as MoveIcon, FolderUp
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import JSZip from 'jszip';
import { ShareFolderModal } from '../components/ShareFolderModal';
import { FilePreviewPanel } from '../components/FilePreviewPanel';
import { FileThumb, getFileCategory } from '../components/FileThumb';

interface StorageFolder {
  id: string;
  name: string;
  parent_id: string | null;
  allowed_roles: string[] | null;
  created_at: string;
}

interface StorageFile {
  id: string;
  folder_id: string | null;
  name: string;
  size_bytes: number;
  mime_type: string | null;
  storage_path: string;
  allowed_roles: string[] | null;
  created_at: string;
}

const BUCKET = 'file-storage';

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Supabase Storage rejects object keys containing characters outside a fairly
// narrow set (accented letters like "é" trigger "Invalid key" errors). The
// display name (DB `name` column) keeps the original file name — only the
// storage key itself needs to be made safe.
const sanitizeStorageSegment = (name: string): string => {
  const withoutDiacritics = name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  return withoutDiacritics.replace(/[^a-zA-Z0-9._\-!*'() ]+/g, '_');
};

// Zip entries macOS/Finder likes to add that nobody wants uploaded.
const isJunkZipEntry = (path: string): boolean => /(^|\/)(__MACOSX|\.DS_Store)(\/|$)/i.test(path);

const SelectionCheckbox: React.FC<{ selected: boolean; onToggle: (e: React.MouseEvent) => void }> = ({ selected, onToggle }) => (
  <button
    onClick={onToggle}
    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
      selected ? 'bg-blue-500 border-blue-500 text-white' : 'bg-gray-900/80 border-gray-600 text-transparent hover:border-blue-400'
    }`}
    title={selected ? 'Clear selection' : 'Select'}
  >
    <Check className="w-3 h-3" />
  </button>
);

export const FileStorage: React.FC = () => {
  const [folders, setFolders] = useState<StorageFolder[]>([]);
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<StorageFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [shareFolder, setShareFolder] = useState<StorageFolder | null>(null);
  const [previewFile, setPreviewFile] = useState<StorageFile | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveDestinationId, setMoveDestinationId] = useState<string | null>(null);
  const [movingFiles, setMovingFiles] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const storageSynced = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const syncStorageMetadata = useCallback(async () => {
    try {
      // Explicit high limits: PostgREST caps unbounded selects at 1000 rows, and a
      // truncated list here would make existing folders/files look missing.
      const [folderRes, fileRes] = await Promise.all([
        supabase.from('storage_folders').select('id').limit(20000),
        // Only the paths: this is purely an "is it already registered?" lookup.
        supabase.from('storage_files').select('storage_path').limit(20000),
      ]);

      if (folderRes.error || fileRes.error) {
        console.error('Could not load file metadata for storage sync:', folderRes.error || fileRes.error);
        return;
      }

      const foldersById = new Set((folderRes.data || []).map((f: { id: string }) => f.id));
      const filesByPath = new Set((fileRes.data || []).map((f: { storage_path: string }) => f.storage_path));

      const registerFile = async (storagePath: string, folderId: string | null, item: any) => {
        if (filesByPath.has(storagePath)) return;
        const metadata = item.metadata || {};
        const { error } = await supabase.from('storage_files').insert({
          folder_id: folderId,
          name: item.name,
          size_bytes: Number(metadata.size || metadata.contentLength || 0),
          mime_type: metadata.mimetype || metadata.contentType || null,
          storage_path: storagePath,
          created_at: item.created_at || new Date().toISOString(),
        });
        if (error) {
          console.error('Could not register storage file:', error);
        } else {
          filesByPath.add(storagePath);
        }
      };

      // Register every file sitting directly under one prefix. Returns the folder
      // ids found as sub-prefixes, so the caller can decide how to walk them.
      const scanPrefix = async (prefix: string, parentId: string | null): Promise<string[]> => {
        const { data, error } = await supabase.storage.from(BUCKET).list(prefix, {
          limit: 1000,
          sortBy: { column: 'name', order: 'asc' },
        });
        if (error || !data) {
          console.error('Could not list storage prefix:', prefix, error);
          return [];
        }

        const subPrefixes: string[] = [];
        for (const item of data as any[]) {
          const storagePath = `${prefix}/${item.name}`;
          const isFolder = item.id === null || item.id === undefined;
          if (isFolder) {
            // This sync exists to recover FILES whose metadata row is missing — it must
            // never create folders. Every folder (nested or not) stores its files under
            // its own flat "folders/<own-id>/" prefix, so each prefix here is simply a
            // folder id: either it still exists (use it as-is, with whatever parent it
            // really has) or it was deleted and these are leftovers to ignore.
            // Creating folders from these prefixes used to spam the root with hundreds
            // of UUID-named folders — one per nested folder, on every sync run.
            if (!foldersById.has(item.name)) {
              console.warn('Ignoring leftover storage prefix of a deleted folder:', storagePath);
              continue;
            }
            subPrefixes.push(item.name);
          } else {
            await registerFile(storagePath, parentId, item);
          }
        }
        return subPrefixes;
      };

      // The app's storage namespace uses `folders/` for managed folders and
      // `root/` for files at the virtual root.
      const [folderIds] = await Promise.all([
        scanPrefix('folders', null),
        scanPrefix('root', null),
      ]);

      // Scan each folder's own prefix with bounded parallelism. There are hundreds of
      // them and each list call costs ~0.2s, so doing this one after another took well
      // over a minute — longer than the interval that re-triggers it.
      const SCAN_CONCURRENCY = 10;
      let next = 0;
      await Promise.all(
        Array.from({ length: Math.min(SCAN_CONCURRENCY, folderIds.length) }, async () => {
          while (next < folderIds.length) {
            const folderId = folderIds[next++];
            await scanPrefix(`folders/${folderId}`, folderId);
          }
        })
      );
    } catch (error) {
      console.error('Storage metadata sync failed:', error);
    }
  }, []);

  // Cheap per-folder variant: one list call for the folder currently on screen.
  // Used while browsing so navigation never waits on a full-bucket scan.
  const syncCurrentFolder = useCallback(async (folderId: string | null): Promise<boolean> => {
    try {
      const prefix = folderId ? `folders/${folderId}` : 'root';
      // `.eq(col, null)` does not match NULL in PostgREST — the root folder needs
      // `.is()`, otherwise every root file would look unregistered and get
      // inserted a second time.
      const knownQuery = supabase.from('storage_files').select('storage_path').limit(20000);
      const [listRes, knownRes] = await Promise.all([
        supabase.storage.from(BUCKET).list(prefix, { limit: 1000, sortBy: { column: 'name', order: 'asc' } }),
        folderId === null ? knownQuery.is('folder_id', null) : knownQuery.eq('folder_id', folderId),
      ]);
      if (listRes.error || !listRes.data) return false;

      const known = new Set((knownRes.data || []).map((f: any) => f.storage_path));
      const missing = (listRes.data as any[]).filter(
        item => item.id !== null && item.id !== undefined && !known.has(`${prefix}/${item.name}`)
      );
      if (missing.length === 0) return false;

      const { error } = await supabase.from('storage_files').insert(
        missing.map(item => ({
          folder_id: folderId,
          name: item.name,
          size_bytes: Number(item.metadata?.size || item.metadata?.contentLength || 0),
          mime_type: item.metadata?.mimetype || item.metadata?.contentType || null,
          storage_path: `${prefix}/${item.name}`,
          created_at: item.created_at || new Date().toISOString(),
        }))
      );
      if (error) {
        console.error('Could not register storage files for folder:', prefix, error);
        return false;
      }
      console.log(`Recovered ${missing.length} unregistered file(s) in ${prefix}`);
      return true;
    } catch (error) {
      console.error('Folder sync failed:', error);
      return false;
    }
  }, []);

  const loadData = useCallback(async (folderId: string | null) => {
    setLoading(true);
    try {
      // Only the files of the folder being viewed — the full table is by far the
      // largest payload here and grows with every upload, while the view never
      // shows more than one folder at a time. Folders are fetched whole: they're
      // a fraction of the size and the tree (breadcrumbs, move targets, recursive
      // delete) needs them. Explicit high limit because PostgREST silently caps
      // unbounded selects at 1000 rows.
      const filesQuery = supabase
        .from('storage_files')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20000);

      const [folderRes, fileRes] = await Promise.all([
        supabase.from('storage_folders').select('*').order('name').limit(20000),
        folderId === null ? filesQuery.is('folder_id', null) : filesQuery.eq('folder_id', folderId),
      ]);

      if (folderRes.error) throw folderRes.error;
      if (fileRes.error) throw fileRes.error;

      setFolders(folderRes.data || []);
      setFiles(fileRes.data || []);

      // Build breadcrumbs
      const crumbs: StorageFolder[] = [];
      let current = folderId;
      const folderMap = new Map((folderRes.data || []).map((f: StorageFolder) => [f.id, f]));
      while (current) {
        const f = folderMap.get(current);
        if (!f) break;
        crumbs.unshift(f);
        current = f.parent_id;
      }
      setBreadcrumbs(crumbs);
    } catch (err) {
      console.error('Error loading file storage:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Render from the metadata tables straight away — that's two quick queries. The
  // storage scans below only ever recover files uploaded outside the app, so they
  // run afterwards in the background instead of holding up the whole view.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadData(currentFolderId);
      if (cancelled) return;

      // Always check the folder being viewed (one list call).
      const recovered = await syncCurrentFolder(currentFolderId);
      if (cancelled) return;
      if (recovered) await loadData(currentFolderId);

      // Full-bucket scan once per session, never on navigation.
      if (!storageSynced.current) {
        storageSynced.current = true;
        await syncStorageMetadata();
        if (!cancelled) await loadData(currentFolderId);
      }
    })();
    return () => { cancelled = true; };
  }, [currentFolderId, loadData, syncStorageMetadata, syncCurrentFolder]);

  // Periodically discover files uploaded directly to Storage without metadata rows.
  // Only for the folder on screen — a full scan here used to take longer than the
  // interval itself, so runs piled up on top of each other indefinitely.
  useEffect(() => {
    const interval = window.setInterval(async () => {
      if (await syncCurrentFolder(currentFolderId)) {
        await loadData(currentFolderId);
      }
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [currentFolderId, loadData, syncCurrentFolder]);

  // Realtime: refresh when someone else uploads files or creates folders
  useEffect(() => {
    const channel = supabase
      .channel('file-storage-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'storage_files' }, () => {
        loadData(currentFolderId);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'storage_folders' }, () => {
        loadData(currentFolderId);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentFolderId, loadData]);

  const visibleFolders = folders.filter(f => f.parent_id === currentFolderId);
  const visibleFiles = files.filter(f => f.folder_id === currentFolderId);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    const { error } = await supabase.from('storage_folders').insert({
      name: newFolderName.trim(),
      parent_id: currentFolderId,
    });
    if (error) {
      console.error('Error creating folder:', error);
      showToast('Failed to create');
      return;
    }
    setNewFolderName('');
    setShowNewFolder(false);
    loadData(currentFolderId);
  };

  const handleRenameFolder = async () => {
    if (!renaming || !renaming.name.trim()) return;
    const { error } = await supabase
      .from('storage_folders')
      .update({ name: renaming.name.trim(), updated_at: new Date().toISOString() })
      .eq('id', renaming.id);
    if (error) {
      console.error('Error renaming folder:', error);
      showToast('Failed to rename');
      return;
    }
    setRenaming(null);
    loadData(currentFolderId);
  };

  // All folder ids in a subtree (the folder itself + every nested descendant).
  // Needed because Storage has no real folders — deleting the DB row alone leaves
  // the actual files behind, which syncStorageMetadata later "rediscovers" and
  // recreates as bogus root folders named after their raw (now orphaned) id.
  const collectFolderSubtreeIds = (rootId: string): string[] => {
    const ids = [rootId];
    const children = folders.filter(f => f.parent_id === rootId);
    for (const child of children) ids.push(...collectFolderSubtreeIds(child.id));
    return ids;
  };

  // Queried rather than read from state: `files` only holds the folder currently on
  // screen, while a delete has to clear out every nested folder's files too.
  const removeSubtreeFilesFromStorage = async (rootFolderId: string) => {
    const subtreeIds = collectFolderSubtreeIds(rootFolderId);
    const { data, error } = await supabase
      .from('storage_files')
      .select('storage_path')
      .in('folder_id', subtreeIds)
      .limit(20000);
    if (error) {
      console.error('Could not look up files to delete:', error);
      return;
    }
    const paths = (data || []).map((f: { storage_path: string }) => f.storage_path);
    if (paths.length === 0) return;
    const { error: storageError } = await supabase.storage.from(BUCKET).remove(paths);
    if (storageError) console.error('Error removing files from storage:', storageError);
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!confirm('Delete folder and all contents?')) return;
    await removeSubtreeFilesFromStorage(folderId);
    const { error } = await supabase.from('storage_folders').delete().eq('id', folderId);
    if (error) {
      console.error('Error deleting folder:', error);
      showToast('Failed to delete');
      return;
    }
    showToast('Folder deleted');
    loadData(currentFolderId);
  };

  const handleDeleteFile = async (file: StorageFile) => {
    if (!confirm(`Delete "${file.name}"?`)) return;
    const { error: storageError } = await supabase.storage.from(BUCKET).remove([file.storage_path]);
    const { error: dbError } = await supabase.from('storage_files').delete().eq('id', file.id);
    if (storageError || dbError) {
      console.error('Error deleting file:', storageError || dbError);
      showToast('Failed to delete');
      return;
    }
    showToast('File deleted');
    loadData(currentFolderId);
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    await uploadFiles(Array.from(fileList));
    event.target.value = '';
  };

  // Folder picker (Finder "Upload Folder") — each File carries webkitRelativePath,
  // e.g. "MyFolder/sub/pic.png", which we use to recreate the folder structure.
  const handleFolderUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    const entries = Array.from(fileList).map(file => ({
      file,
      relativePath: (file as any).webkitRelativePath || file.name,
    }));
    await uploadEntries(entries);
    event.target.value = '';
  };

  // Recursively reads a dropped FileSystemEntry (file or directory) into a flat
  // list of { file, relativePath }, preserving the folder structure Finder gave it.
  const readEntry = (entry: any, basePath: string): Promise<{ file: File; relativePath: string }[]> => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file: File) => resolve([{ file, relativePath: `${basePath}${entry.name}` }]));
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const collected: any[] = [];
        const readBatch = () => {
          reader.readEntries(async (batch: any[]) => {
            if (batch.length === 0) {
              const nested = await Promise.all(
                collected.map(child => readEntry(child, `${basePath}${entry.name}/`))
              );
              resolve(nested.flat());
            } else {
              collected.push(...batch);
              readBatch();
            }
          });
        };
        readBatch();
      } else {
        resolve([]);
      }
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);

    const items = e.dataTransfer.items;
    const firstEntry = items && items.length > 0 ? (items[0] as any).webkitGetAsEntry?.() : null;
    if (firstEntry) {
      const nested = await Promise.all(
        Array.from(items).map(item => {
          const entry = (item as any).webkitGetAsEntry?.();
          return entry ? readEntry(entry, '') : Promise.resolve([]);
        })
      );
      const entries = nested.flat();
      if (entries.length > 0) {
        await uploadEntries(entries);
        return;
      }
    }

    const fileList = e.dataTransfer.files;
    if (!fileList || fileList.length === 0) return;
    await uploadFiles(Array.from(fileList));
  };

  // Back-compat wrapper: plain file list, uploaded directly into the current folder.
  const uploadFiles = async (fileList: File[]) => {
    await uploadEntries(fileList.map(file => ({ file, relativePath: file.name })));
  };

  // Expands any .zip files in the list into their contained files, nested under a
  // folder named after the zip (so "Songs.zip" -> a "Songs" folder with its contents).
  const expandZipEntries = async (
    entries: { file: File; relativePath: string }[]
  ): Promise<{ file: File; relativePath: string }[]> => {
    const expanded: { file: File; relativePath: string }[] = [];
    for (const entry of entries) {
      if (!/\.zip$/i.test(entry.file.name)) {
        expanded.push(entry);
        continue;
      }
      try {
        const zip = await JSZip.loadAsync(entry.file);
        const baseDir = entry.relativePath.replace(/\.zip$/i, '');
        for (const [zipPath, zipEntry] of Object.entries(zip.files)) {
          if (zipEntry.dir || isJunkZipEntry(zipPath)) continue;
          const blob = await zipEntry.async('blob');
          const name = zipPath.split('/').pop() || zipPath;
          // globalThis.File, not the "File" icon imported above from lucide-react.
          expanded.push({ file: new globalThis.File([blob], name), relativePath: `${baseDir}/${zipPath}` });
        }
      } catch (err) {
        console.error('ZIP extraction failed for', entry.file.name, err);
        showToast(`Could not extract ${entry.file.name}`);
      }
    }
    return expanded;
  };

  // Single upload pipeline for everything: plain files, dropped/picked folders, and
  // ZIP archives. relativePath directory segments become (or reuse) real folders
  // under currentFolderId; the file name itself is sanitized for the storage key.
  const uploadEntries = async (rawEntries: { file: File; relativePath: string }[]) => {
    if (rawEntries.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const entries = await expandZipEntries(rawEntries);
      if (entries.length === 0) {
        showToast('Nothing to upload');
        return;
      }

      // Cache of already-resolved folders, keyed by "<parentId|root>/<name>", seeded
      // with folders already known so we reuse existing ones instead of duplicating them.
      const folderCache = new Map<string, string | null>();
      for (const f of folders) {
        folderCache.set(`${f.parent_id ?? 'root'}/${f.name}`, f.id);
      }

      const resolveFolder = async (segments: string[]): Promise<string | null> => {
        let parentId: string | null = currentFolderId;
        for (const segment of segments) {
          const key = `${parentId ?? 'root'}/${segment}`;
          let id = folderCache.get(key);
          if (id === undefined) {
            const { data, error } = await supabase
              .from('storage_folders')
              .insert({ name: segment, parent_id: parentId })
              .select('id')
              .single();
            if (error || !data) {
              console.error('Could not create folder', segment, error);
              return null;
            }
            id = data.id;
            folderCache.set(key, id);
          }
          parentId = id;
        }
        return parentId;
      };

      let uploaded = 0;
      for (const { file, relativePath } of entries) {
        const segments = relativePath.split('/').filter(Boolean);
        segments.pop(); // drop the file name, keep directory segments only
        const targetFolderId = segments.length > 0 ? await resolveFolder(segments) : currentFolderId;
        if (segments.length > 0 && targetFolderId === null) {
          console.error('Skipping file, could not resolve folder for:', relativePath);
          continue;
        }

        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 8);
        const safeName = sanitizeStorageSegment(file.name);
        const storagePath = targetFolderId
          ? `folders/${targetFolderId}/${timestamp}_${randomString}_${safeName}`
          : `root/${timestamp}_${randomString}_${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(storagePath, file, { cacheControl: '3600', upsert: false, contentType: file.type });
        if (uploadError) {
          console.error('Upload error for', relativePath, uploadError);
          continue;
        }

        const { error: dbError } = await supabase.from('storage_files').insert({
          folder_id: targetFolderId,
          name: file.name,
          size_bytes: file.size,
          mime_type: file.type || null,
          storage_path: storagePath,
        });
        if (dbError) console.error('Metadata insert error for', relativePath, dbError);

        uploaded++;
        setUploadProgress(Math.round((uploaded / entries.length) * 100));
      }
      showToast(`${uploaded} file${uploaded !== 1 ? 's' : ''} uploaded`);
      await loadData(currentFolderId);
    } catch (err) {
      console.error('Upload error:', err);
      showToast(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fallback for non-secure contexts / iframe restrictions
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        return true;
      } catch {
        return false;
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  const copyPublicLink = async (file: StorageFile) => {
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(file.storage_path);
    const ok = await copyToClipboard(data.publicUrl);
    if (ok) showToast('Link kopiert');
    else showToast(data.publicUrl);
  };

  const downloadFile = async (file: StorageFile) => {
    // Use the download parameter on the public URL so the browser
    // downloads the file instead of navigating/opening it inline.
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(file.storage_path, { download: file.name });
    const a = document.createElement('a');
    a.href = data.publicUrl;
    a.download = file.name;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedFiles = files.filter(f => selectedIds.has(f.id));
  const selectedFolders = folders.filter(f => selectedIds.has(f.id));

  const downloadSelectedAsZip = async () => {
    if (selectedFiles.length === 0) return;
    setDownloadingZip(true);
    try {
      const zip = new JSZip();
      for (const file of selectedFiles) {
        const { data } = supabase.storage.from(BUCKET).getPublicUrl(file.storage_path);
        const res = await fetch(data.publicUrl);
        if (!res.ok) continue;
        const blob = await res.blob();
        zip.file(file.name, blob);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'files.zip';
      a.click();
      URL.revokeObjectURL(a.href);
      showToast('ZIP created');
    } catch (err) {
      console.error('ZIP error:', err);
      showToast('ZIP failed');
    } finally {
      setDownloadingZip(false);
    }
  };

  const downloadFolderAsZip = async () => {
    if (visibleFiles.length === 0) return;
    setDownloadingZip(true);
    try {
      const zip = new JSZip();
      for (const file of visibleFiles) {
        const { data } = supabase.storage.from(BUCKET).getPublicUrl(file.storage_path);
        const res = await fetch(data.publicUrl);
        if (!res.ok) continue;
        const blob = await res.blob();
        zip.file(file.name, blob);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${breadcrumbs[breadcrumbs.length - 1]?.name || 'Folder'}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      showToast('ZIP created');
    } catch (err) {
      console.error('ZIP error:', err);
      showToast('ZIP failed');
    } finally {
      setDownloadingZip(false);
    }
  };

  const getFolderLabel = (folder: StorageFolder): string => {
    const parent = folder.parent_id ? folders.find(item => item.id === folder.parent_id) : null;
    return parent ? `${getFolderLabel(parent)} / ${folder.name}` : folder.name;
  };

  const moveSelectedFiles = async () => {
    if (selectedFiles.length === 0) return;
    setMovingFiles(true);
    let moved = 0;
    try {
      const destinationPrefix = moveDestinationId ? `folders/${moveDestinationId}` : 'root';

      for (const file of selectedFiles) {
        const fileName = file.storage_path.split('/').pop() || file.name;
        const nextPath = `${destinationPrefix}/${fileName}`;
        if (nextPath === file.storage_path && file.folder_id === moveDestinationId) {
          moved++;
          continue;
        }

        const { error: moveError } = await supabase.storage
          .from(BUCKET)
          .move(file.storage_path, nextPath);
        if (moveError) {
          console.error('Storage move error:', moveError);
          continue;
        }

        const { error: metadataError } = await supabase
          .from('storage_files')
          .update({ folder_id: moveDestinationId, storage_path: nextPath })
          .eq('id', file.id);
        if (metadataError) {
          console.error('File metadata move error:', metadataError);
          // Keep the file discoverable at its new Storage location on the next sync.
          continue;
        }
        moved++;
      }

      showToast(`${moved} file${moved !== 1 ? 's' : ''} moved`);
      setSelectedIds(new Set());
      setShowMoveModal(false);
      setMoveDestinationId(null);
      await loadData(currentFolderId);
    } finally {
      setMovingFiles(false);
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const isFolderSelected = (id: string) => selectedIds.has(id);

  const deleteSelected = async () => {
    const fileCount = selectedFiles.length;
    const folderCount = selectedFolders.length;
    if (fileCount === 0 && folderCount === 0) return;
    const parts = [
      fileCount > 0 ? `${fileCount} file${fileCount !== 1 ? 's' : ''}` : null,
      folderCount > 0 ? `${folderCount} folder${folderCount !== 1 ? 's' : ''} (with all contents)` : null,
    ].filter(Boolean).join(' and ');
    if (!confirm(`Delete ${parts}? This cannot be undone.`)) return;

    setDeletingSelected(true);
    try {
      if (fileCount > 0) {
        const paths = selectedFiles.map(f => f.storage_path);
        const { error: storageError } = await supabase.storage.from(BUCKET).remove(paths);
        if (storageError) console.error('Error removing files from storage:', storageError);
        const { error: dbError } = await supabase.from('storage_files').delete().in('id', selectedFiles.map(f => f.id));
        if (dbError) console.error('Error deleting file metadata:', dbError);
      }
      for (const folder of selectedFolders) {
        await removeSubtreeFilesFromStorage(folder.id);
        const { error } = await supabase.from('storage_folders').delete().eq('id', folder.id);
        if (error) console.error('Error deleting folder:', folder.name, error);
      }
      showToast(`Deleted ${fileCount + folderCount} item${fileCount + folderCount !== 1 ? 's' : ''}`);
      clearSelection();
      await loadData(currentFolderId);
    } catch (err) {
      console.error('Bulk delete failed:', err);
      showToast('Delete failed');
    } finally {
      setDeletingSelected(false);
    }
  };

  return (
    <div className="p-6 min-h-screen">
      {/* Toast */}
      {toast && (
        <div className="fixed top-20 right-4 z-50 bg-gray-800 border border-gray-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg">
          {toast}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">File Storage</h1>
            <p className="text-sm text-gray-400 mt-1">Folders, files and public links</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedFiles.length > 0 && (
              <button
                onClick={() => setShowMoveModal(true)}
                className="btn-outline !px-3 !py-2"
                title="Move selected files"
              >
                <MoveIcon className="w-4 h-4" />
                Move ({selectedFiles.length})
              </button>
            )}
            {(selectedFiles.length > 0 || selectedFolders.length > 0) && (
              <button
                onClick={deleteSelected}
                disabled={deletingSelected}
                className="flex items-center gap-2 px-3 py-2 bg-red-600/10 hover:bg-red-600/20 text-red-500 text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete selected"
              >
                {deletingSelected ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete ({selectedFiles.length + selectedFolders.length})
              </button>
            )}
            {selectedFiles.length >= 2 ? (
              <>
                <button
                  onClick={downloadSelectedAsZip}
                  disabled={downloadingZip}
                  className="btn disabled:opacity-50 disabled:cursor-not-allowed !px-3 !py-2"
                >
                  {downloadingZip ? <Loader className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
                  ZIP ({selectedFiles.length})
                </button>
                <button
                  onClick={clearSelection}
                  className="btn-outline !px-3 !py-2"
                >
                  Clear selection
                </button>
              </>
            ) : (selectedFiles.length > 0 || selectedFolders.length > 0) ? (
              <button
                onClick={clearSelection}
                className="btn-outline !px-3 !py-2"
              >
                Clear selection
              </button>
            ) : (
              visibleFiles.length > 0 && (
                <button
                  onClick={downloadFolderAsZip}
                  disabled={downloadingZip}
                  className="btn disabled:opacity-50 disabled:cursor-not-allowed !px-3 !py-2"
                  title="Download all files in this folder as ZIP"
                >
                  {downloadingZip ? <Loader className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
                  Download All
                </button>
              )
            )}
            <div className="flex items-center bg-gray-100 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
                title="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn !px-3 !py-2"
              title="Upload files or .zip archives (zips are extracted automatically)"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            <button
              onClick={() => folderInputRef.current?.click()}
              className="btn-outline !px-3 !py-2"
              title="Upload a folder from Finder, preserving its structure"
            >
              <FolderUp className="w-4 h-4" />
              Upload folder
            </button>
            <button
              onClick={() => setShowNewFolder(true)}
              className="btn-outline !px-3 !py-2"
            >
              <Plus className="w-4 h-4" />
              New folder
            </button>
            {currentFolderId && breadcrumbs.length > 0 && (
              <button
                onClick={() => setShareFolder(breadcrumbs[breadcrumbs.length - 1])}
                className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg transition-colors"
                title="Share current folder"
              >
                <Share2 className="w-4 h-4" />
                Share folder
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-4 items-start">
          <div className="flex-1 min-w-0">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleUpload}
        />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFolderUpload}
          // webkitdirectory/directory aren't in React's typed DOM attributes but are
          // supported by every Chromium/Safari/Firefox desktop build.
          {...{ webkitdirectory: 'true', directory: 'true' } as any}
        />

        {/* Upload progress */}
        {uploading && (
          <div className="mb-4 bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between text-xs text-gray-300 mb-2">
              <span className="flex items-center gap-2"><Loader className="w-3 h-3 animate-spin" /> Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {/* Breadcrumbs */}
        <div className="flex items-center gap-1 mb-4 flex-wrap">
          <button
            onClick={() => setCurrentFolderId(null)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${currentFolderId === null ? 'text-primary-600 dark:text-white bg-primary-50 dark:bg-primary-900/20' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          >
            <FolderOpen className="w-4 h-4" />
            Root
          </button>
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => setCurrentFolderId(crumb.id)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${i === breadcrumbs.length - 1 ? 'text-primary-600 dark:text-white bg-primary-50 dark:bg-primary-900/20' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                <Folder className="w-4 h-4" />
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
          {currentFolderId !== null && (
            <>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => setCurrentFolderId(null)}
                className="flex items-center gap-1 px-2 py-1 rounded text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* New folder form */}
        {showNewFolder && (
          <div className="mb-4 bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
              <Folder className="w-4 h-4 text-white" />
            </div>
            <input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); } }}
              placeholder="Folder name..."
              className="flex-1 px-3 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={handleCreateFolder}
              className="btn !px-3 !py-1.5"
            >
              Create
            </button>
            <button
              onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}
              className="p-1.5 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Drag & Drop overlay */}
        {dragging && (
          <div
            className="fixed inset-0 z-40 bg-primary-500/10 border-2 border-dashed border-primary-500 rounded-lg pointer-events-none"
            onDragOver={(e) => e.preventDefault()}
          >
            <div className="flex items-center justify-center h-full text-primary-500 text-lg font-medium">
              Drop files here
            </div>
          </div>
        )}

        {/* Content */}
        <div
          className={`border-2 border-dashed rounded-xl p-4 min-h-[400px] transition-colors ${dragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-700'}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : visibleFolders.length === 0 && visibleFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500 dark:text-gray-400">
              <div className="w-14 h-14 rounded-2xl bg-primary-500 flex items-center justify-center mb-3">
                <FolderOpen className="w-7 h-7 text-white" />
              </div>
              <p className="text-sm">Empty folder</p>
              <p className="text-xs mt-1">Drag files here or click Upload</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid [grid-template-columns:repeat(auto-fill,minmax(150px,150px))] justify-start gap-2">
              {/* Folders */}
              {visibleFolders.map(folder => (
                <div
                  key={folder.id}
                  onClick={() => setCurrentFolderId(folder.id)}
                  className={`group bg-white dark:bg-dark-800 border rounded-lg overflow-hidden cursor-pointer transition-all hover:border-primary-500 ${isFolderSelected(folder.id) ? 'border-primary-500' : 'border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="relative aspect-square bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isFolderSelected(folder.id) ? 'bg-primary-600' : 'bg-primary-500'}`}>
                      <Folder className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute top-2 right-2">
                      <SelectionCheckbox
                        selected={isFolderSelected(folder.id)}
                        onToggle={(e) => { e.stopPropagation(); toggleSelect(folder.id); }}
                      />
                    </div>
                  </div>
                  <div className="p-3">
                    {renaming?.id === folder.id ? (
                      <input
                        autoFocus
                        value={renaming.name}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFolder(); if (e.key === 'Escape') setRenaming(null); }}
                        className="w-full px-2 py-1 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-sm text-white font-medium truncate" title={folder.name}>{folder.name}</p>
                    )}
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className="text-xs text-gray-500">Folder</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); setShareFolder(folder); }}
                          className="p-1 rounded hover:bg-blue-500/20 text-gray-400 hover:text-blue-400 transition-colors"
                          title="Share folder"
                        >
                          <Share2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setRenaming({ id: folder.id, name: folder.name }); }}
                          className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                          title="Rename"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                          className="p-1 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Files */}
              {visibleFiles.map(file => {
                const isSelected = selectedIds.has(file.id);
                const isPreviewing = previewFile?.id === file.id;
                return (
                  <div
                    key={file.id}
                    onClick={() => setPreviewFile(file)}
                    className={`group relative bg-white dark:bg-dark-800 border rounded-lg overflow-hidden cursor-pointer transition-all hover:border-primary-500 ${isSelected ? 'border-primary-500' : isPreviewing ? 'border-gray-400' : 'border-gray-200 dark:border-gray-700'}`}
                  >
                    <div className="relative">
                      <FileThumb file={file} size="md" />
                      <div className="absolute top-2 right-2">
                        <SelectionCheckbox
                          selected={isSelected}
                          onToggle={(e) => { e.stopPropagation(); toggleSelect(file.id); }}
                        />
                      </div>
                    </div>
                    <div className="p-2.5">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={file.name}>{file.name}</p>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getFileCategory(file).classes}`}>
                            {getFileCategory(file).label}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400">
                            {formatBytes(file.size_bytes)}
                          </span>
                        </span>
                        <div className={`flex items-center gap-1 transition-opacity ${isPreviewing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyPublicLink(file); }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            title="Copy public link"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadFile(file); }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            title="Download"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteFile(file); }}
                            className="p-1 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* List view */
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 px-2 w-8"></th>
                    <th className="py-2 px-2">Name</th>
                    <th className="py-2 px-2">Size</th>
                    <th className="py-2 px-2 hidden sm:table-cell">Date</th>
                    <th className="py-2 px-2 w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFolders.map(folder => (
                    <tr
                      key={folder.id}
                      onClick={() => setCurrentFolderId(folder.id)}
                      className={`group border-b border-gray-200 dark:border-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/40 transition-colors ${isFolderSelected(folder.id) ? 'bg-primary-500/5' : ''}`}
                    >
                      <td className="py-2 px-2">
                        <SelectionCheckbox
                          selected={isFolderSelected(folder.id)}
                          onToggle={(e) => { e.stopPropagation(); toggleSelect(folder.id); }}
                        />
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isFolderSelected(folder.id) ? 'bg-primary-600' : 'bg-primary-500'}`}>
                            <Folder className="w-3.5 h-3.5 text-white" />
                          </div>
                          <span className="text-gray-900 dark:text-white truncate">{folder.name}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-gray-500 dark:text-gray-400">Folder</td>
                      <td className="py-2 px-2 hidden sm:table-cell text-gray-500 dark:text-gray-400">{formatDate(folder.created_at)}</td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); setShareFolder(folder); }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            title="Share folder"
                          >
                            <Share2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setRenaming({ id: folder.id, name: folder.name }); }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            title="Rename"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                            className="p-1 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleFiles.map(file => {
                    const isSelected = selectedIds.has(file.id);
                    return (
                      <tr
                        key={file.id}
                        onClick={() => setPreviewFile(file)}
                        className={`group border-b border-gray-200 dark:border-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/40 transition-colors ${isSelected ? 'bg-primary-500/5' : ''}`}
                      >
                        <td className="py-2 px-2">
                          <SelectionCheckbox
                            selected={isSelected}
                            onToggle={(e) => { e.stopPropagation(); toggleSelect(file.id); }}
                          />
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileThumb file={file} size="sm" />
                            <span className="text-gray-900 dark:text-white truncate">{file.name}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <span className="flex items-center gap-1.5 text-xs">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${getFileCategory(file).classes}`}>
                              {getFileCategory(file).label}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400">{formatBytes(file.size_bytes)}</span>
                          </span>
                        </td>
                        <td className="py-2 px-2 hidden sm:table-cell text-gray-500 dark:text-gray-400">{formatDate(file.created_at)}</td>
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => { e.stopPropagation(); copyPublicLink(file); }}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                              title="Copy public link"
                            >
                              <Link2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); downloadFile(file); }}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                              title="Download"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteFile(file); }}
                              className="p-1 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-500 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Multi-select hint */}
        <p className="text-xs text-gray-500 dark:text-gray-600 mt-4">
          Select via the checkbox in the top-right corner — then download multiple files as ZIP.
        </p>
          </div>

          {/* Preview panel */}
          {previewFile && (
            <FilePreviewPanel file={previewFile} onClose={() => setPreviewFile(null)} />
          )}
        </div>
      </div>

      {/* Move selected files modal */}
      {showMoveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowMoveModal(false)}>
          <div
            className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-700 rounded-xl w-full max-w-md p-5 shadow-xl"
            onClick={event => event.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
                  <MoveIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Move files</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{selectedFiles.length} selected</p>
                </div>
              </div>
              <button
                onClick={() => setShowMoveModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <label className="block text-sm text-gray-700 dark:text-gray-300">
              Destination folder
              <select
                value={moveDestinationId || ''}
                onChange={event => setMoveDestinationId(event.target.value || null)}
                className="mt-1.5 w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Root</option>
                {folders
                  .filter(folder => folder.id !== currentFolderId)
                  .sort((a, b) => getFolderLabel(a).localeCompare(getFolderLabel(b)))
                  .map(folder => (
                    <option key={folder.id} value={folder.id}>{getFolderLabel(folder)}</option>
                  ))}
              </select>
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setShowMoveModal(false)}
                className="btn-outline !px-3 !py-2"
              >
                Cancel
              </button>
              <button
                onClick={moveSelectedFiles}
                disabled={movingFiles}
                className="btn disabled:opacity-50 disabled:cursor-not-allowed !px-3 !py-2"
              >
                {movingFiles ? <Loader className="w-4 h-4 animate-spin" /> : <MoveIcon className="w-4 h-4" />}
                Move files
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share folder modal */}
      {shareFolder && (
        <ShareFolderModal
          folderId={shareFolder.id}
          folderName={shareFolder.name}
          onClose={() => setShareFolder(null)}
        />
      )}
    </div>
  );
};
